import { apiError } from "../../../../../../utils/formErrors";
import React, { useState, useEffect, useMemo } from "react";
import { Box, Stack, Select, MenuItem, Checkbox, CircularProgress } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useParams, useNavigate } from "react-router";
import TopBar from "../../../../../../component/TopBar";
import BtnPrimary from "../../../../../../component/PrimaryButton";
import BtnSecondary from "../../../../../../component/SecondaryButton";
import FormField from "../../../../../../component/Formfield";
import TypeBtn from "../../../../../../component/AddRemoveButton";
import SectionLoader from "../../../../../../component/SectionLoader";
import { WizardCard, FormSection, FieldLabel } from "../../../../../../component/wizard";
import { selectSx } from "../../../../../../component/listing";
import { fetchEmployeesByIdArray } from "../../../../../../services/employee";
import { getStructureByEmployee, saveSalaryStructure, getSalaryTemplates, getSalaryComponents, getStatutoryComponents } from "../../../../../../services/payroll";
import { getActiveLegalUnitId } from "../../../../../../services/globalData";
import { useNotify } from "../../../../../../component/NotificationProvider";
import { tokens, displayFont } from "../../../../../../theme";
import { money } from "../payrollUtils";

import { pressable } from "../../../../../../component/pressableProps";
import InlineMessage from "../../../../../../component/InlineMessage";
const COMPONENTS = ["EARNING", "DEDUCTION", "REIMBURSEMENT"];
const CALC = ["FIXED", "PERCENTAGE"];
const BASES = ["CTC", "BASIC"];
const round = (n) => Math.round((Number(n) || 0) * 100) / 100;
// component-row column template: name | bucket | type | value | base | tax | residual | monthly | delete
const GRID = "1.6fr 1fr 0.95fr 0.7fr 0.85fr 2.5rem 3rem 6.5rem 2rem";

const DEFAULT_ROWS = [
  { name: "Basic", component: "EARNING", calculationType: "PERCENTAGE", value: 40, percentageOf: "CTC", taxable: true, isResidual: false },
  { name: "HRA", component: "EARNING", calculationType: "PERCENTAGE", value: 50, percentageOf: "BASIC", taxable: true, isResidual: false },
  { name: "Special Allowance", component: "EARNING", calculationType: "FIXED", value: 0, percentageOf: "CTC", taxable: true, isResidual: true },
];

// Normalize a statutory config (new generic or legacy) into the calc shape — mirrors backend.
const LEGACY_STAT = {
  EPF: { baseField: "BASIC", ceiling: 15000, ceilingMode: "CAP" },
  ESI: { baseField: "GROSS", ceiling: 21000, ceilingMode: "ELIGIBILITY" },
  PROFESSIONAL_TAX: { calcBasis: "FIXED" }, LABOURW_WELFARE_FUND: { calcBasis: "FIXED" },
};
const normStat = (c) => {
  const lg = LEGACY_STAT[c.component] || {};
  return {
    label: c.name || lg.label || c.component || "Statutory",
    calcBasis: c.calcBasis || lg.calcBasis || "PERCENT",
    baseField: c.baseField || lg.baseField || "BASIC",
    ceiling: (c.ceiling != null && c.ceiling !== 0) ? c.ceiling : (c.wageCeiling || lg.ceiling || 0),
    ceilingMode: c.ceilingMode || lg.ceilingMode || "NONE",
    employeeRate: c.employeeRate || 0, employerRate: c.employerRate || 0, amount: c.amount || 0,
  };
};

// Mirror of backend resolveStructure: CTC includes employer statutory; a residual
// earning plugs to gross so gross + employer == CTC. Generic over statutory configs.
const resolvePreview = (ctc, rows, statutory) => {
  const lines = rows.map((r) => ({ ...r, annualAmount: 0, monthlyAmount: 0 }));
  const isEarning = (l) => l.component !== "DEDUCTION";
  const annualOf = (l, basic) => l.calculationType === "FIXED" ? Number(l.value || 0)
    : ((l.percentageOf === "BASIC" ? basic : ctc) * Number(l.value || 0)) / 100;
  lines.forEach((l) => { if (!l.isResidual && (l.calculationType === "FIXED" || l.percentageOf === "CTC")) l.annualAmount = round(annualOf(l, 0)); });
  const basic = lines.find((l) => isEarning(l) && /^basic/i.test(l.name))?.annualAmount || 0;
  lines.forEach((l) => { if (!l.isResidual && l.calculationType === "PERCENTAGE" && l.percentageOf === "BASIC") l.annualAmount = round(annualOf(l, basic)); });

  const hasResidual = lines.some((l) => isEarning(l) && l.isResidual);
  const active = (statutory || []).filter((s) => s.isActive !== false).map(normStat);
  let grossAnnual, annualEmployer = 0;
  if (hasResidual) {
    const employerOnBasic = active.filter((n) => n.calcBasis === "PERCENT" && n.baseField === "BASIC" && n.employerRate)
      .reduce((s, n) => { const cap = n.ceilingMode === "CAP" && n.ceiling ? n.ceiling * 12 : Infinity; return s + (Math.min(basic, cap) * n.employerRate) / 100; }, 0);
    const grossER = active.filter((n) => n.calcBasis === "PERCENT" && n.baseField === "GROSS" && n.employerRate);
    const rate = (set) => set.reduce((r, n) => r + n.employerRate / 100, 0);
    grossAnnual = round((ctc - employerOnBasic) / (1 + rate(grossER)));
    const applicable = grossER.filter((n) => !(n.ceilingMode === "ELIGIBILITY" && n.ceiling && grossAnnual / 12 > n.ceiling));
    grossAnnual = round((ctc - employerOnBasic) / (1 + rate(applicable)));
    active.forEach((n) => {
      if (n.calcBasis !== "PERCENT" || !n.employerRate) return;
      let b = n.baseField === "GROSS" ? grossAnnual : basic;
      if (n.ceiling) { if (n.ceilingMode === "ELIGIBILITY" && b / 12 > n.ceiling) return; if (n.ceilingMode === "CAP") b = Math.min(b, n.ceiling * 12); }
      annualEmployer += (b * n.employerRate) / 100;
    });
    annualEmployer = round(annualEmployer);
    const nonResid = lines.filter((l) => isEarning(l) && !l.isResidual).reduce((s, l) => s + l.annualAmount, 0);
    const resid = lines.find((l) => isEarning(l) && l.isResidual);
    resid.annualAmount = round(grossAnnual - nonResid);
  }
  lines.forEach((l) => { l.monthlyAmount = round(l.annualAmount / 12); });
  const earn = lines.filter(isEarning).reduce((s, l) => s + l.annualAmount, 0);
  const ded = lines.filter((l) => !isEarning(l)).reduce((s, l) => s + l.annualAmount, 0);
  const taxable = lines.filter((l) => isEarning(l) && l.taxable).reduce((s, l) => s + l.annualAmount, 0);
  const residualLine = lines.find((l) => isEarning(l) && l.isResidual);
  return {
    lines,
    monthlyGross: round(earn / 12), monthlyDeductions: round(ded / 12), monthlyNet: round((earn - ded) / 12),
    annualGross: round(earn), annualEmployer, taxableAnnual: round(taxable),
    hasResidual, over: hasResidual && residualLine && residualLine.annualAmount < 0,
    reconciles: hasResidual ? Math.abs(round(earn + annualEmployer) - ctc) < 1 : null,
  };
};

// monthly employee + employer statutory from the previewed basic/gross (generic)
const computeStatutoryPreview = (basicMonthly, grossMonthly, statutory) => {
  const employee = [], employer = [];
  (statutory || []).forEach((c) => {
    if (c.isActive === false) return;
    const n = normStat(c);
    if (n.calcBasis === "FIXED") { if (n.amount) employee.push({ name: n.label, amount: round(n.amount) }); return; }
    let base = n.baseField === "GROSS" ? grossMonthly : basicMonthly;
    if (n.ceiling) { if (n.ceilingMode === "ELIGIBILITY" && base > n.ceiling) return; if (n.ceilingMode === "CAP") base = Math.min(base, n.ceiling); }
    const ee = round((base * n.employeeRate) / 100), er = round((base * n.employerRate) / 100);
    if (ee) employee.push({ name: `${n.label} (Employee)`, amount: ee });
    if (er) employer.push({ name: `${n.label} (Employer)`, amount: er });
  });
  return { employee, employer };
};

// build initial component rows from the catalog's active earnings (residual on
// the first "Special Allowance"-like line); falls back to the hardcoded set.
const rowsFromCatalog = (catalog) => {
  const earnings = (catalog || []).filter((c) => c.component !== "DEDUCTION" && c.isActive !== false);
  if (earnings.length === 0) return DEFAULT_ROWS;
  let residualIdx = earnings.findIndex((c) => /special|allowance|residual/i.test(c.name));
  if (residualIdx === -1) residualIdx = earnings.length - 1;
  return earnings.map((c, i) => ({
    name: c.name, component: c.component || "EARNING",
    calculationType: i === residualIdx ? "FIXED" : (c.calculationType || "FIXED"),
    value: i === residualIdx ? 0 : (c.value || 0),
    percentageOf: c.percentageOf || "CTC", taxable: c.taxable !== false,
    isResidual: i === residualIdx, sourceComponent: c._id,
  }));
};

const SalaryStructureForm = () => {
  const { employeeId } = useParams();
  const navigate = useNavigate();
  const notify = useNotify();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [employee, setEmployee] = useState(null);
  const [ctc, setCtc] = useState("");
  const [rows, setRows] = useState(DEFAULT_ROWS);
  const [templates, setTemplates] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [statutory, setStatutory] = useState([]);
  const [exemptions, setExemptions] = useState([]); // statutory _ids this employee is exempt from
  const [templateId, setTemplateId] = useState("");

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      const lu = getActiveLegalUnitId();
      try {
        const [empRes, structRes, tplRes, compRes, statRes] = await Promise.all([
          fetchEmployeesByIdArray([employeeId]),
          getStructureByEmployee(employeeId),
          getSalaryTemplates(lu).catch(() => null),
          getSalaryComponents(lu).catch(() => null),
          getStatutoryComponents(lu).catch(() => null),
        ]);
        if (!active) return;
        setEmployee(empRes?.data?.[0] || null);
        setTemplates((Array.isArray(tplRes?.data) ? tplRes.data : []).filter((t) => !t.isDeleted));
        const cat = (Array.isArray(compRes?.data) ? compRes.data : []).filter((c) => !c.isDeleted);
        setCatalog(cat);
        setStatutory((Array.isArray(statRes?.data) ? statRes.data : []).filter((s) => !s.isDeleted));
        const s = structRes?.data;
        if (s) {
          setCtc(String(s.ctc));
          setRows((s.components || []).map((c) => ({ name: c.name, component: c.component, calculationType: c.calculationType, value: c.value, percentageOf: c.percentageOf, taxable: c.taxable !== false, isResidual: !!c.isResidual, sourceComponent: c.sourceComponent })));
          setExemptions((s.statutoryExemptions || []).map(String));
        } else {
          // new structure: seed rows from the org's catalog earnings, not hardcoded defaults
          setRows(rowsFromCatalog(cat));
        }
      } catch (e) {
        notify.error(e?.message || "Failed to load structure");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [employeeId]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyTemplate = (id) => {
    setTemplateId(id);
    const t = templates.find((x) => x._id === id);
    if (!t) return;
    const mapped = (t.linkedComponent || []).filter((c) => typeof c === "object").map((c) => ({
      name: c.name, component: c.component || "EARNING", calculationType: c.calculationType || "FIXED",
      value: c.value || 0, percentageOf: c.percentageOf || "CTC", taxable: c.taxable !== false, isResidual: false, sourceComponent: c._id,
    }));
    if (mapped.length === 0) { notify.error("Template has no components"); return; }
    if (!mapped.some((m) => m.component !== "DEDUCTION")) { notify.error("Template has no earnings"); return; }
    setRows(mapped);
    notify.success(`Applied template: ${t.name} — mark one earning as residual`);
  };

  const addFromCatalog = (id) => {
    const c = catalog.find((x) => x._id === id);
    if (!c) return;
    setRows((p) => [...p, { name: c.name, component: c.component || "EARNING", calculationType: c.calculationType || "FIXED", value: c.value || 0, percentageOf: c.percentageOf || "CTC", taxable: c.taxable !== false, isResidual: false, sourceComponent: c._id }]);
  };

  // statutory after per-employee exemptions — drives both preview & save reconciliation
  const effectiveStatutory = useMemo(() => statutory.filter((s) => !exemptions.includes(String(s._id))), [statutory, exemptions]);
  const preview = useMemo(() => resolvePreview(Number(ctc) || 0, rows, effectiveStatutory), [ctc, rows, effectiveStatutory]);
  const toggleExempt = (id) => setExemptions((p) => (p.includes(String(id)) ? p.filter((x) => x !== String(id)) : [...p, String(id)]));
  const setRow = (i, f, v) => setRows((p) => p.map((r, idx) => (idx === i ? { ...r, [f]: v } : r)));
  // residual is exclusive across earnings
  const setResidual = (i) => setRows((p) => p.map((r, idx) => ({ ...r, isResidual: idx === i ? !r.isResidual : false })));

  const save = async () => {
    if (!ctc || Number(ctc) <= 0) { notify.error("Enter a valid CTC"); return; }
    const components = rows.filter((r) => r.name).map((r) => ({
      name: r.name, component: r.component, calculationType: r.calculationType,
      value: Number(r.value) || 0, percentageOf: r.percentageOf, taxable: r.taxable !== false,
      isResidual: !!r.isResidual, sourceComponent: r.sourceComponent,
    }));
    if (components.length === 0) { notify.error("Add at least one component"); return; }
    if (preview.over) { notify.error("Components exceed CTC after employer contributions — reduce them"); return; }
    setSaving(true);
    try {
      const res = await saveSalaryStructure({ employee: employeeId, ctc: Number(ctc), components, statutoryExemptions: exemptions });
      if (res?.success === false) throw apiError(res, "Could not save structure");
      notify.success("Salary structure saved");
      navigate("/app/settings/payroll/structures");
    } catch (e) {
      notify.error(e?.message || "Could not save structure");
    } finally {
      setSaving(false);
    }
  };

  const name = employee ? `${employee.fname || ""} ${employee.lname || ""}`.trim() : "Employee";

  return (
    <Box sx={{ width: "100%" }}>
      <TopBar MainHeading={"Payroll"} breadcrumbs={[{ label: "Salary Structures", href: "/app/settings/payroll/structures" }, { label: name }]} />
      <WizardCard>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: "1.5rem" }}>
          <Box sx={{ fontFamily: displayFont, fontSize: "1.25rem", fontWeight: 600 }}>{name} — Salary Structure</Box>
          <BtnSecondary to="/app/settings/payroll/structures" sx={{ borderRadius: "8px" }}>Back</BtnSecondary>
        </Stack>

        {loading ? <SectionLoader /> : (
          <>
            <FormSection title="Annual CTC" description="Total cost to company = gross earnings + employer statutory contributions.">
              <Box sx={{ maxWidth: "20rem" }}>
                <FieldLabel required>CTC (₹ / year)</FieldLabel>
                <FormField type="number" value={ctc} onChange={(e) => setCtc(e.target.value)} fullwidth />
              </Box>
            </FormSection>

            <FormSection title="Components" description="Pick from the catalog or define inline. Mark one earning as the residual — it absorbs the remainder so earnings + employer cost = CTC.">
              <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: "1.25rem", flexWrap: "wrap" }}>
                {catalog.some((c) => c.component !== "DEDUCTION") && (
                  <Select value="" onChange={(e) => addFromCatalog(e.target.value)} size="small" displayEmpty sx={{ ...selectSx, minWidth: "14rem" }}>
                    <MenuItem value="" disabled>+ Add from catalog…</MenuItem>
                    {catalog.filter((c) => c.component !== "DEDUCTION").map((c) => <MenuItem key={c._id} value={c._id}>{c.name} ({(c.component || "").toLowerCase()})</MenuItem>)}
                  </Select>
                )}
                {templates.length > 0 && (
                  <Select value={templateId} onChange={(e) => applyTemplate(e.target.value)} size="small" displayEmpty sx={{ ...selectSx, minWidth: "13rem" }}>
                    <MenuItem value="" disabled>Apply template…</MenuItem>
                    {templates.map((t) => <MenuItem key={t._id} value={t._id}>{t.name}</MenuItem>)}
                  </Select>
                )}
              </Stack>

              <Box sx={{ overflowX: "auto" }}>
                <Box sx={{ minWidth: "60rem" }}>
                  {/* header row */}
                  <Box sx={{ display: "grid", gridTemplateColumns: GRID, columnGap: "0.75rem", px: "0.75rem", pb: "0.5rem" }}>
                    {["Component", "Bucket", "Type", "Value", "Base", "Tax", "Residual", "Monthly", ""].map((h, i) => (
                      <Box key={i} sx={{ fontSize: "0.8125rem", fontWeight: 600, color: tokens.textMuted, textTransform: "uppercase", letterSpacing: "0.03em", textAlign: i === 7 ? "right" : i >= 5 && i <= 6 ? "center" : "left" }}>{h}</Box>
                    ))}
                  </Box>
                  {/* component rows */}
                  <Stack spacing={0.5}>
                    {rows.map((r, i) => (
                      <Box key={i} sx={{ display: "grid", gridTemplateColumns: GRID, columnGap: "0.75rem", alignItems: "center",
                        p: "0.5rem 0.75rem", borderRadius: "10px",
                        border: `1px solid ${r.isResidual ? `${tokens.accent}66` : tokens.border}`,
                        background: r.isResidual ? `${tokens.accentTint}` : tokens.card }}>
                        <FormField type="text" value={r.name} onChange={(e) => setRow(i, "name", e.target.value)} fullwidth noMargin height="2.75rem" />
                        <Select value={r.component} onChange={(e) => setRow(i, "component", e.target.value)} size="small" sx={{ ...selectSx, width: "100%" }}>
                          {COMPONENTS.map((c) => <MenuItem key={c} value={c}>{c.charAt(0) + c.slice(1).toLowerCase()}</MenuItem>)}
                        </Select>
                        <Select value={r.calculationType} disabled={r.isResidual} onChange={(e) => setRow(i, "calculationType", e.target.value)} size="small" sx={{ ...selectSx, width: "100%" }}>
                          {CALC.map((c) => <MenuItem key={c} value={c}>{c === "FIXED" ? "Fixed ₹" : "% of"}</MenuItem>)}
                        </Select>
                        <FormField type="number" value={r.isResidual ? "" : r.value} placeholder={r.isResidual ? "auto" : ""} disabled={r.isResidual} onChange={(e) => setRow(i, "value", e.target.value)} fullwidth noMargin height="2.75rem" />
                        <Select value={r.percentageOf} onChange={(e) => setRow(i, "percentageOf", e.target.value)} size="small" disabled={r.calculationType === "FIXED" || r.isResidual} sx={{ ...selectSx, width: "100%" }}>
                          {BASES.map((b) => <MenuItem key={b} value={b}>{b}</MenuItem>)}
                        </Select>
                        <Box sx={{ display: "flex", justifyContent: "center" }}>
                          <Checkbox checked={r.taxable !== false} disabled={r.component === "DEDUCTION"} onChange={() => setRow(i, "taxable", !r.taxable)} size="small" sx={{ p: 0 }} />
                        </Box>
                        <Box sx={{ display: "flex", justifyContent: "center" }}>
                          <Checkbox checked={!!r.isResidual} disabled={r.component === "DEDUCTION"} onChange={() => setResidual(i)} size="small" sx={{ p: 0, color: r.isResidual ? tokens.accent : undefined }} />
                        </Box>
                        <Box sx={{ textAlign: "right", fontSize: "0.875rem", fontWeight: 600, color: r.component === "DEDUCTION" ? tokens.danger : tokens.text, whiteSpace: "nowrap" }}>{money(preview.lines[i]?.monthlyAmount)}</Box>
                        <Box {...pressable(() => setRows((p) => p.filter((_, idx) => idx !== i)), "Remove")} sx={{ display: "flex", justifyContent: "center", cursor: "pointer", color: tokens.textMuted, "&:hover": { color: tokens.danger } }}><DeleteOutlineIcon sx={{ fontSize: "1.1rem" }} /></Box>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              </Box>
              <Box sx={{ mt: "0.75rem" }}><TypeBtn type="add" onClick={() => setRows([...rows, { name: "", component: "EARNING", calculationType: "FIXED", value: 0, percentageOf: "CTC", taxable: true, isResidual: false }])}>Add Component</TypeBtn></Box>
            </FormSection>

            {statutory.length > 0 && (() => {
              const basicMo = preview.lines.find((l) => l.component !== "DEDUCTION" && /^basic/i.test(l.name))?.monthlyAmount || 0;
              return (
                <FormSection title="Statutory" description="Configured org-wide and applied automatically each run. Untick to exempt this employee (e.g. PF opt-out).">
                  <Stack spacing={0.75}>
                    {statutory.map((c) => {
                      const exempt = exemptions.includes(String(c._id));
                      // compute this one component's amounts (so we can show per-line)
                      const r = computeStatutoryPreview(basicMo, preview.monthlyGross, [c]);
                      const ee = r.employee.reduce((s, x) => s + x.amount, 0);
                      const er = r.employer.reduce((s, x) => s + x.amount, 0);
                      const applies = ee > 0 || er > 0;
                      return (
                        <Stack key={c._id} direction="row" alignItems="center" spacing={1.5}
                          sx={{ p: "0.75rem 1rem", border: `1px solid ${tokens.border}`, borderRadius: "10px", opacity: exempt ? 0.5 : 1, background: tokens.card }}>
                          <Checkbox checked={!exempt} onChange={() => toggleExempt(c._id)} size="small" sx={{ p: 0 }} />
                          <Box sx={{ flex: 1, fontSize: "0.875rem", fontWeight: 600, textDecoration: exempt ? "line-through" : "none" }}>{c.name || c.component}</Box>
                          {!applies && !exempt && <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>not eligible at this pay</Box>}
                          {ee > 0 && <Box sx={{ fontSize: "0.8125rem", color: tokens.danger }}>EE {money(ee)}/mo</Box>}
                          {er > 0 && <Box sx={{ fontSize: "0.8125rem", color: tokens.accent }}>ER {money(er)}/mo</Box>}
                        </Stack>
                      );
                    })}
                  </Stack>
                </FormSection>
              );
            })()}

            {!preview.hasResidual && (
              <InlineMessage tone="warning" sx={{ mb: "1.25rem" }}>
            No residual earning marked — earnings won't reconcile to CTC (the remainder is unallocated). Tick "Residual" on one earning (usually Special Allowance).
          </InlineMessage>
            )}
            {preview.over && (
              <InlineMessage tone="danger" sx={{ mb: "1.25rem" }}>
            Components exceed CTC after employer contributions — reduce them before saving.
          </InlineMessage>
            )}

            <Box sx={{ background: tokens.surfaceSubtle, border: `1px solid ${tokens.border}`, borderRadius: "12px", p: "1.25rem", mb: "1.5rem" }}>
              <Stack direction="row" justifyContent="space-around" textAlign="center" flexWrap="wrap" gap={2}>
                <Box><Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>Monthly Gross</Box><Box sx={{ fontFamily: displayFont, fontSize: "1.25rem", fontWeight: 600 }}>{money(preview.monthlyGross)}</Box></Box>
                <Box><Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>Employer / mo</Box><Box sx={{ fontFamily: displayFont, fontSize: "1.25rem", fontWeight: 600, color: tokens.accent }}>{money(round(preview.annualEmployer / 12))}</Box></Box>
                <Box><Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>Taxable / yr</Box><Box sx={{ fontFamily: displayFont, fontSize: "1.25rem", fontWeight: 600 }}>{money(preview.taxableAnnual)}</Box></Box>
                <Box><Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>Gross + Employer (yr)</Box><Box sx={{ fontFamily: displayFont, fontSize: "1.25rem", fontWeight: 600, color: preview.reconciles ? tokens.successText : tokens.text }}>{money(preview.annualGross + preview.annualEmployer)}{preview.reconciles ? " ✓" : ""}</Box></Box>
              </Stack>
            </Box>

            <Stack direction="row" justifyContent="flex-end" spacing={1.5}>
              <BtnSecondary to="/app/settings/payroll/structures" sx={{ borderRadius: "8px" }}>Cancel</BtnSecondary>
              <BtnPrimary onClick={save} disabled={saving} sx={{ borderRadius: "8px" }}>{saving ? <CircularProgress size={"1.25rem"} /> : "Save Structure"}</BtnPrimary>
            </Stack>
          </>
        )}
      </WizardCard>
    </Box>
  );
};

export default SalaryStructureForm;
